import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';
import { ConsultationRequest } from '@tassomutuo-backoffice/types';
import { DetailFieldList, DetailField } from '@tassomutuo-backoffice/ui';
import { getConsultationRequest } from '../../../../services/api/consultation-requests/consultation-requests';
import useDateFormat from '../../../../hooks/useDateFormat';
import ConsultationServiceTypeChip, {
  SERVICE_TYPE_LABEL,
} from './ConsultationServiceTypeChip';

interface IProps {
  open: boolean;
  requestId: string | null;
  onClose: () => void;
}

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '85%', md: '75%', lg: '65%' },
  maxWidth: 900,
  maxHeight: '90vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 1,
  p: 3,
};

const SectionHeader = ({ title }: { title: string }) => (
  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
    {title}
  </Typography>
);

const fullPhone = (prefix?: string, phone?: string) =>
  prefix && phone ? `${prefix} ${phone}` : prefix || phone || undefined;

const yesNo = (value?: boolean) =>
  value === undefined ? undefined : value ? 'Sì' : 'No';

const useConsultationRequest = (id: string | null) => {
  return useQuery<ConsultationRequest>({
    queryKey: ['consultationRequest', id],
    queryFn: () => {
      if (!id) return Promise.reject(new Error('Missing id'));
      return getConsultationRequest(id);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

const ConsultationRequestDetailModal = ({
  open,
  requestId,
  onClose,
}: IProps) => {
  const { formatDate } = useDateFormat();
  const { data, isLoading, error } = useConsultationRequest(requestId);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error || !data) {
      return (
        <Alert severity="error">
          Impossibile caricare il dettaglio della richiesta di consulenza.
        </Alert>
      );
    }

    const request = data;

    const contatto: DetailField[] = [
      { label: 'Nome', value: request.fullName },
      { label: 'Email', value: request.email },
      {
        label: 'Telefono',
        value: fullPhone(request.phonePrefix, request.phone),
      },
    ];

    const origine: DetailField[] = [
      {
        label: 'Servizio',
        value: SERVICE_TYPE_LABEL[request.serviceType] ?? request.serviceType,
      },
      { label: 'Pagina', value: request.sourcePage },
      {
        label: 'Agenzia',
        value: request.agenzia_euroansa_da_cui_arriva_la_lead,
      },
      { label: 'Consulente', value: request.consulente_richiesto_pagina },
      { label: 'Segnalatore', value: request.segnalatore },
    ];

    const consenso: DetailField[] = [
      { label: 'Termini', value: yesNo(request.acceptTerms) },
      {
        label: 'Data consenso',
        value: request.acceptTermsAt
          ? formatDate(request.acceptTermsAt, 'dd/MM/yyyy HH:mm')
          : undefined,
      },
    ];

    const tecnici: DetailField[] = [
      { label: 'ID', value: request.id },
      {
        label: 'Creata il',
        value: request.createdAt
          ? formatDate(request.createdAt, 'dd/MM/yyyy HH:mm')
          : undefined,
      },
      {
        label: 'Aggiornata il',
        value: request.updatedAt
          ? formatDate(request.updatedAt, 'dd/MM/yyyy HH:mm')
          : undefined,
      },
    ];

    return (
      <Stack spacing={3}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <ConsultationServiceTypeChip serviceType={request.serviceType} />
          {request.createdAt && (
            <Typography variant="body2" color="text.secondary">
              creata il {formatDate(request.createdAt, 'dd/MM/yyyy HH:mm')}
            </Typography>
          )}
        </Stack>

        <Box>
          <SectionHeader title="Contatto" />
          <DetailFieldList fields={contatto} />
        </Box>

        <Divider />

        <Box>
          <SectionHeader title="Origine" />
          <DetailFieldList fields={origine} />
        </Box>

        <Divider />

        <Box>
          <SectionHeader title="Consenso" />
          <DetailFieldList fields={consenso} />
        </Box>

        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 0,
              borderTop: (th) => `1px solid ${th.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Dati tecnici
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <DetailFieldList fields={tecnici} />
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Dettaglio richiesta di consulenza
          </Typography>
          <IconButton onClick={onClose}>
            <ClearIcon color="primary" />
          </IconButton>
        </Box>
        {renderBody()}
      </Box>
    </Modal>
  );
};

export default ConsultationRequestDetailModal;
